// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, CircularProgress, DialogActions, FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material'
import { FC, useContext, useEffect } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
// component import
import { Alert } from '../../../common/Alert'
import { CustomController } from '../../../common/CustomController'
import { CustomModal } from '../../../common/CustomModal'
// others
import { ExternalUserStatus, useCreateExternalUserMutation } from '../../../../../generated'
import { CANCEL, US_STATES } from '../../../../constants'
import { AppContext, CaseContext } from '../../../../context'
import { AddNewExternalUserProps, CreateNewAdversary, ErrorException } from '../../../../interfaceTypes'
import { titleize } from '../../../../utils'
import { addNewExternalUserValidationSchema } from '../../../../validationSchema'
import { CaseActionType } from '../../../cases/caseReducer'
import { ActionType } from '../../../../context/AppContextReducer'
import { InputFormFields } from '../../../externalUsers/InputFormFields'

export const AddNewAdversary: FC<AddNewExternalUserProps> = ({
  setNewExternalUser,
  refetch,
  handleCustomClose,
  customOpen
}) => {
  const { adversaryRoleId, dispatch } = useContext(CaseContext)
  const { isOpen: isNewAdversaryOpen, dispatch: appDispatch } = useContext(AppContext)
  const defaultState = US_STATES.filter(state => state.name === 'New Jersey')

  const methods = useForm<CreateNewAdversary>({
    mode: 'all',
    resolver: yupResolver(addNewExternalUserValidationSchema),

    defaultValues: {
      firstName: '',
      lastName: '',
      businessName: '',
      status: ExternalUserStatus.Active,
      externalUserRoleId: adversaryRoleId,
      phoneNumber: '',
      email: '',
      addresses: [
        {
          address1: '',
          address2: '',
          country: 'USA',
          county: '',
          externalUserId: '',
          state: defaultState[0].name
        }
      ]
    }
  })

  const [createExternalUserMutation, { loading: creteExternalUserLoader }] = useCreateExternalUserMutation({
    onError({ graphQLErrors }) {
      if (graphQLErrors) {
        graphQLErrors.map(({ extensions }) => {
          if (extensions) {
            const { exception } = extensions
            if (exception) {
              const { response } = exception as ErrorException
              const { response: errorResponse } = response || {}
              if (errorResponse) {
                const { error } = errorResponse
                return Alert.error(error)
              }
            }
          }
          return null
        })
      }
    },

    onCompleted(data) {
      if (data) {
        const {
          createExternalUser: { response, externalUser }
        } = data || {}

        if (response) {
          const { status } = response

          if (status === 200 && externalUser) {
            Alert.success('External User Created Successfully')
            setNewExternalUser && setNewExternalUser(externalUser)

            if (isNewAdversaryOpen || customOpen) handleClose()
          }
        }
        refetch?.()
      }
    }
  })

  const { handleSubmit, control, reset, setValue } = methods

  const handleClose = () => {
    if (handleCustomClose) handleCustomClose()
    else {
      dispatch({ type: CaseActionType.SET_NEW_ADVERSARY_OPEN, isNewAdversaryOpen: false })
      appDispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isNewAdversaryOpen })
    }
    reset()
  }

  const onSubmit: SubmitHandler<CreateNewAdversary> = async (data: CreateNewAdversary) => {
    const { firstName, lastName, phoneNumber, homePhone, personalPhone, dateOfBirth, ...rest } = data

    await createExternalUserMutation({
      variables: {
        createExternalUserInput: {
          ...rest,
          dateOfBirth: !!dateOfBirth ? dateOfBirth : undefined,
          firstName: firstName?.trim() || undefined,
          lastName: lastName?.trim() || undefined,
          externalUserRoleId: adversaryRoleId,
          phoneNumber: phoneNumber ?? undefined,
          homePhone: homePhone ?? undefined,
          personalPhone: personalPhone ?? undefined
        }
      }
    })
  }

  useEffect(() => {
    if (adversaryRoleId) setValue('externalUserRoleId', adversaryRoleId)
    // eslint-disable-next-line
  }, [adversaryRoleId, isNewAdversaryOpen])

  return (
    <CustomModal title="Add New Adversary" isOpen={customOpen ?? !!isNewAdversaryOpen} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form
          onSubmit={event => {
            event.stopPropagation()
            return handleSubmit(onSubmit)(event)
          }}
        >
          <Grid container spacing={2} rowSpacing={2}>
            <InputFormFields />

            <Grid item md={12} sm={12} xs={12}>
              <CustomController
                controllerName="addresses[0].address1"
                controllerLabel="Address line 1"
                fieldType="text"
              />
            </Grid>

            <Grid item md={12} sm={12} xs={12}>
              <CustomController
                controllerName="addresses[0].address2"
                controllerLabel="Address line 2"
                fieldType="text"
              />
            </Grid>

            <Grid item md={4} sm={12} xs={12}>
              <CustomController controllerName="addresses[0].city" controllerLabel="City" fieldType="text" />
            </Grid>

            <Grid item md={4} sm={12} xs={12}>
              <FormControl variant="outlined" fullWidth sx={{ marginTop: 1 }}>
                <InputLabel id="address-state">State</InputLabel>

                <Controller
                  name="addresses.0.state"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="address-state"
                      label="State"
                      fullWidth
                      MenuProps={{ style: { maxHeight: 300 } }}
                    >
                      {US_STATES.map((item, index) => {
                        const { name } = item || {}
                        return (
                          <MenuItem value={name} key={`${item}-${index}`}>
                            {titleize(name)}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item md={2} sm={12} xs={12}>
              <CustomController
                controllerName="addresses[0].country"
                controllerLabel="Country"
                fieldType="text"
                readOnly
              />
            </Grid>

            <Grid item md={2} sm={12} xs={12}>
              <CustomController controllerName="addresses.0.zipCode" controllerLabel="Zip Code" fieldType="text" />
            </Grid>
          </Grid>

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose} variant="outlined" aria-label={CANCEL}>
              {CANCEL}
            </Button>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={creteExternalUserLoader}
              aria-label="add new adversary"
              endIcon={creteExternalUserLoader && <CircularProgress size={20} color="inherit" />}
            >
              Add
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}
